import { RoutePlanRunStatsDTO } from 'api/model';
import React from 'react';
import styled from 'styled-components';
import { formatDurationMs } from './selection-helpers';
import { getManualReasonLabel } from './warning-helpers';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const Value = styled.span<{ tone?: 'good' | 'bad' }>`
  font-size: 1.4em;
  font-weight: 700;
  color: ${({ tone }) =>
    tone === 'good' ? 'rgb(0,150,0)' : tone === 'bad' ? 'rgb(255,0,0)' : 'inherit'};
`;

const Label = styled.span`
  font-size: 0.8em;
  color: rgb(120, 120, 120);
`;

const BudgetWarning = styled.div`
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 3px;
  background: #ffbf00;
  font-size: 0.85em;
  font-weight: 600;
`;

/**
 * Amber, inte rött: talen nedan betyder inte att körningen är felaktig, utan att den
 * beskriver mindre än man tror — en FT utan hemankare planeras inte alls, och ett paket
 * som fick någon annan än matrisens val har avvikit från minsta totala tomkörning.
 */
const Notes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.85em;
  color: rgb(138, 98, 6);
`;

/**
 * Neutral ton: de här raderna beskriver hur körningen gick, inte att något är fel. Amber hade
 * läst som en varning om ett hemresepass som gjorde precis det det skulle.
 */
const RunNotes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.85em;
  color: rgb(90, 90, 90);
`;

interface Props {
  stats: RoutePlanRunStatsDTO;
}

const StatsBar: React.FC<Props> = ({ stats }) => {
  const totalOccupiedMs = stats.totalOccupiedHours * 3_600_000;
  const totalTransferMs = stats.totalTransferHours * 3_600_000;
  const budgetReached = stats.googleCallsUsed >= stats.googleCallBudget;

  return (
    <Wrapper>
      <Row>
        <Stat>
          <Value>{stats.packageCount}</Value>
          <Label>Paket</Label>
        </Stat>
        <Stat>
          <Value tone="good">
            {stats.coveredAssignments} / {stats.poolSize}
          </Value>
          <Label>Täckta uppdrag</Label>
        </Stat>
        <Stat>
          <Value tone="good">{stats.autoPackageCount}</Value>
          <Label>Redo att tillämpa</Label>
        </Stat>
        <Stat>
          <Value tone={stats.manualPackageCount > 0 ? 'bad' : undefined}>
            {stats.manualPackageCount}
          </Value>
          <Label>Behöver input</Label>
        </Stat>
        <Stat>
          <Value tone={stats.unplannableCount > 0 ? 'bad' : undefined}>
            {stats.unplannableCount}
          </Value>
          <Label>Oplanerbara</Label>
        </Stat>
        <Stat>
          <Value tone={stats.fieldTestersConsidered === 0 ? 'bad' : undefined}>
            {stats.fieldTestersConsidered}
          </Value>
          <Label>Fälttestare i matchningen</Label>
        </Stat>
        <Stat>
          <Value>{formatDurationMs(totalTransferMs)}</Value>
          <Label>Total körtid (överföringar)</Label>
        </Stat>
        <Stat>
          <Value>{formatDurationMs(totalOccupiedMs)}</Value>
          <Label>Total arbetstid</Label>
        </Stat>
        {budgetReached && (
          <BudgetWarning>
            ⚠ Ruttanrop: budget nådd ({stats.googleCallsUsed}/{stats.googleCallBudget}) —
            resterande tider är uppskattade
          </BudgetWarning>
        )}
      </Row>
      {(stats.manualReasonCounts.length > 0 ||
        stats.returnLegsRepaired > 0 ||
        stats.returnLegsNeedingCustomerRebooking > 0) && (
        <RunNotes>
          {stats.manualReasonCounts.length > 0 && (
            <span>
              Behöver input av:{' '}
              {stats.manualReasonCounts
                .map((c) => `${getManualReasonLabel(c.reason).toLowerCase()} ${c.count}`)
                .join(' · ')}
            </span>
          )}
          {stats.returnLegsRepaired > 0 && (
            <span>
              Hemresepasset flyttade {stats.returnLegsRepaired} ben till paket som annars hade
              strandat borta. Hur det gick till står i &quot;Så togs förslaget fram&quot;.
            </span>
          )}
          {stats.returnLegsNeedingCustomerRebooking > 0 && (
            <span>
              {stats.returnLegsNeedingCustomerRebooking} hemresa/hemresor finns men kräver att
              kunden bokas om först — planeringen ändrar aldrig en bokad tid själv.
            </span>
          )}
        </RunNotes>
      )}
      {(stats.fieldTestersWithoutHomeAnchor > 0 ||
        stats.packagesNotMatchingGlobalAssignment > 0 ||
        stats.swapCandidatesTooLongForTwoParties > 0) && (
        <Notes>
          {stats.fieldTestersWithoutHomeAnchor > 0 && (
            <span>
              {stats.fieldTestersWithoutHomeAnchor} fälttestare saknar hemankare och ingick
              inte i planeringen. Sätt hemankaret på personalkortet under Anställda.
            </span>
          )}
          {stats.packagesNotMatchingGlobalAssignment > 0 && (
            <span>
              {stats.packagesNotMatchingGlobalAssignment} paket fick någon annan än den
              globala tilldelningens val — den tilldelade föll på en gate eller hann bokas
              på ett tidigare paket.
            </span>
          )}
          {stats.swapCandidatesTooLongForTwoParties > 0 && (
            <span>
              {stats.swapCandidatesTooLongForTwoParties} långrutter var för långa för att
              delas av två förare, och kunde därför inte skiftas.
            </span>
          )}
        </Notes>
      )}
    </Wrapper>
  );
};

export default StatsBar;
